import {
  type InputHTMLAttributes,
  type RefObject,
  useCallback,
  useMemo,
  useRef,
  useState,
} from "react";
import Masonry from "react-responsive-masonry";
import { Button } from "../../../components/Button/Button.tsx";
import { Dialog } from "../../../components/Dialog/Dialog.tsx";
import { Icon } from "../../../components/Icon/Icon.tsx";
import { useImageDropZone } from "../../../hooks/useImageDropZone.ts";
import { MODEL_TYPE_LABELS } from "../../Board/display.ts";
import { STYLE_MODEL_TYPE } from "../../constants.ts";
import { ModelCreationImageTile } from "../components/ModelCreationImageTile.tsx";
import { ModelCreationLayout } from "../components/ModelCreationLayout.tsx";
import { ModelFormColumn } from "../components/ModelFormColumn.tsx";
import { TipsSection } from "../components/TipsSection.tsx";
import { useAddImageOnModel } from "../hooks/useAddImageOnModel.ts";
import { useModel } from "../hooks/useModel.ts";

export const ModelTrainingImagesForm = ({
  onClickNext,
}: {
  onClickNext: () => void;
}) => {
  const { model } = useModel();
  const uploadButtonRef = useRef<HTMLDivElement>(null);
  const [isConfirmDialogOpen, setIsConfirmDialogOpen] = useState(false);
  const { mutate: uploadImage, isLoading } = useAddImageOnModel({
    onUploaded: () => {
      uploadButtonRef.current?.scrollIntoView({
        behavior: "smooth",
      });
    },
  });

  const { getRootProps, getInputProps, open } = useImageDropZone({
    multiple: true,
    minImageSize: 512,
    onImageValidated: (file) => {
      if (file) uploadImage({ image: file.fileToUpload });
    },
  });

  const hasNotEnoughImages = useMemo(() => model.contents.length < 4, [model]);

  const hasFewImages = useMemo(
    () => model.contents.length >= 4 && model.contents.length < 10,
    [model],
  );

  const handleNextClick = useCallback(() => {
    if (hasFewImages) {
      setIsConfirmDialogOpen(true);
    } else {
      onClickNext();
    }
  }, [hasFewImages, onClickNext]);

  return (
    <ModelCreationLayout
      title={
        model.type === STYLE_MODEL_TYPE
          ? "Upload at least 4 images with the same vibe"
          : `Upload at least 4 images to train your ${MODEL_TYPE_LABELS[
              model.type
            ].toLowerCase()}`
      }
      {...getRootProps()}
    >
      <div className="flex-col items-center gap-200">
        <ModelFormColumn>
          <div className="flex-col gap-1200">
            <TipsSection modelType={model.type} />
            <TrainingImageGrid />
            <div className="flex-col gap-200">
              <div className="flex-col gap-600">
                <DropZone
                  uploadButtonRef={uploadButtonRef}
                  getInputProps={getInputProps}
                  open={open}
                  isLoading={isLoading}
                />
              </div>
            </div>
          </div>
        </ModelFormColumn>
      </div>
      <div className="flex-col gap-200 items-center">
        <ModelFormColumn>
          {hasFewImages && <NumberOfImagesWarning />}
          <Button
            className="w-full"
            size="md"
            disabled={hasNotEnoughImages}
            onClick={handleNextClick}
          >
            Next
          </Button>
        </ModelFormColumn>
        <TermsAndConditions />
      </div>

      <ConfirmationDialog
        isOpen={isConfirmDialogOpen}
        onOpenChange={setIsConfirmDialogOpen}
        onContinue={onClickNext}
      />
    </ModelCreationLayout>
  );
};

const ConfirmationDialog = ({
  isOpen,
  onOpenChange,
  onContinue,
}: {
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
  onContinue: () => void;
}) => (
  <Dialog
    isOpen={isOpen}
    onOpenChange={onOpenChange}
    title="Upload more images to get high-quality results"
    className="w-[400px]"
    content={
      <div className="flex-col gap-400 p-400">
        <div className="body-md-default">
          Uploading fewer than 10 images may result in inconsistent and
          lower-quality generations. Do you want to continue or go back to
          upload more images?
        </div>
        <div className="flex-row gap-300 justify-end">
          <Button variant="secondary" onClick={() => onOpenChange(false)}>
            Back to upload
          </Button>
          <Button
            onClick={() => {
              onOpenChange(false);
              onContinue();
            }}
          >
            Continue
          </Button>
        </div>
      </div>
    }
  />
);

const TrainingImageGrid = () => {
  const { model } = useModel();
  return (
    model.contents.length > 0 && (
      <div className="flex-col gap-400">
        <Masonry columnsCount={4} gutter="16px">
          {model.contents.map((image) => (
            <ModelCreationImageTile
              key={image.uuid}
              image={image}
              modelUuid={model.uuid}
            />
          ))}
        </Masonry>
      </div>
    )
  );
};

const NumberOfImagesWarning = () => (
  <div className="flex-row-center gap-200 w-full p-300 rounded-200">
    <Icon name="TriangleAlert" className="stroke-error-rest" size="sm" />
    <span className="body-md-semibold text-error-secondary">
      Uploading fewer than 10 images may result in inconsistent and
      lower-quality generations.
    </span>
  </div>
);

// FIXME: Duplicate, need to be harmonized with the one in the Board section
const DropZone = ({
  uploadButtonRef,
  getInputProps,
  open,
  isLoading,
}: {
  uploadButtonRef: RefObject<HTMLDivElement>;
  getInputProps: () => InputHTMLAttributes<HTMLInputElement>;
  open: () => void;
  isLoading: boolean;
}) => (
  <div
    ref={uploadButtonRef}
    className="max-h-[250px] flex-grow flex-col-center w-full gap-400 py-1000 px-800 rounded-300 border-025 border-dashed border-input-border-rest bg-surface-primary-rest"
  >
    <input {...getInputProps()} />
    <Icon name="ImagePlus" size="lg" />
    <span className="body-lg-semibold text-primary">Drop images here or</span>
    <Button
      type="button"
      variant="primary"
      onClick={() => open()}
      loading={isLoading}
    >
      Browse files
    </Button>
  </div>
);

const TermsAndConditions = () => (
  <div className="flex-col-center text-center text-primary body-lg-default">
    <div>
      Your pictures are used exclusively to train this model. See our&nbsp;
      <a
        href="https://gopimento.notion.site/Pimento-Data-protection-and-generated-images-usage-rights-summary-bbfdc8ac0c234eab8dff6a2b73a7e0b3"
        target="_blank"
        className="underline"
      >
        Brand Safety and No Training policy
      </a>
      .
    </div>
    <div>
      You agree that the uploaded images do not violate our&nbsp;
      <a
        href="https://pimentodesign.notion.site/terms-and-conditions"
        target="_blank"
        className="underline"
      >
        Terms and Conditions
      </a>
      .
    </div>
  </div>
);
